import { callFirebaseFunction } from './firebaseCalls';

/**
 * Global Settings Service
 * 
 * Provides access to global application settings stored in Firestore.
 * This replaces the need for environment variables and allows runtime
 * configuration changes by global administrators.
 */

let cachedSettings: any = null;
let settingsPromise: Promise<any> | null = null;

/**
 * Get global settings from the database
 * @param {boolean} forceRefresh - Force refresh from database
 * @returns {Promise<Object>} Global settings object
 */
export const getGlobalSettings = async (forceRefresh: boolean = false): Promise<any> => {
  // Return cached settings if available and not forcing refresh
  if (cachedSettings && !forceRefresh) {
    return cachedSettings;
  }

  // If there's already a pending request, return it
  if (settingsPromise && !forceRefresh) {
    return settingsPromise;
  }

  try {
    settingsPromise = callFirebaseFunction('readGlobalSettings') as Promise<any>;

    const result = await settingsPromise;

    if (result.success) {
      cachedSettings = result.settings;
      console.log('DEBUG: Global settings loaded from database:', cachedSettings);
      return cachedSettings;
    } else {
      console.error('DEBUG: Firebase function failed:', result.error);
      throw new Error(result.error || 'Failed to load global settings');
    }
  } catch (error) {
    console.error('Error loading global settings:', error);
    // Return fallback settings if database is unavailable
    const fallback = getFallbackSettings();
    console.log('DEBUG: Using fallback global settings:', fallback);
    return fallback;
  } finally {
    settingsPromise = null;
  }
};

/**
 * Get fallback settings when database is unavailable
 * @returns {Object} Fallback settings
 */
const getFallbackSettings = (): any => {
  return {
    app: {
      name: process.env.REACT_APP_APP_NAME || 'App-Template',
      description: process.env.REACT_APP_APP_DESCRIPTION || 'Multi-tenant SaaS platform',
      version: process.env.REACT_APP_VERSION || '1.0.0',
      subtitle: process.env.REACT_APP_APP_SUBTITLE || 'Your framework to develop production ready applications',
      platformTagline: process.env.REACT_APP_PLATFORM_TAGLINE || 'Multi-tenant SaaS Platform',
      companyName: process.env.REACT_APP_COMPANY_NAME || 'Your Company'
    },
    firebase: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
    },
    features: {
      analytics: true,
      feedback: true,
      referrals: true
    },
    modules: {
      // EvalPro modules
      evaluationSchemas: {
        enabled: true,
        version: '1.0.0'
      },
      consultants: {
        enabled: true,
        version: '1.0.0'
      },
      presentations: {
        enabled: true,
        version: '1.0.0'
      },
      analytics: {
        enabled: true,
        version: '1.0.0'
      },
      reports: {
        enabled: true,
        version: '1.0.0'
      },
      // Template modules
      todo: {
        enabled: true,
        version: '1.0.0',
        settings: {
          allowAssignments: true,
          allowPriorities: true,
          allowDueDates: true,
          maxTodosPerUser: 1000
        }
      },
      documents: {
        enabled: false,
        version: '1.0.0',
        settings: {
          maxFileSize: 10 * 1024 * 1024,
          allowedTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx'],
          storagePath: 'documents'
        }
      },
      calendar: {
        enabled: false,
        version: '1.0.0',
        settings: {
          allowRecurring: true,
          allowReminders: true,
          maxEventsPerUser: 1000
        }
      },
      billing: {
        enabled: false,
        version: '1.0.0',
        settings: {
          currency: 'USD',
          taxRate: 0.0,
          plans: ['basic', 'pro', 'enterprise']
        }
      }
    },
    security: {
      passwordMinLength: 8,
      requireEmailVerification: true,
      allowGoogleSignIn: true,
      sessionTimeout: 24 * 60 * 60 * 1000,
      maxLoginAttempts: 5,
      lockoutDuration: 15 * 60 * 1000
    },
    analytics: {
      enabled: true,
      provider: 'firebase',
      trackUserBehavior: true,
      trackPerformance: true,
      anonymizeData: false
    },
    ui: {
      theme: {
        primary: '#2563eb',
        secondary: '#2563eb',
        background: '#f8fafc',
        text: '#111827'
      },
      layout: {
        sidebarWidth: 240,
        headerHeight: 64,
        mobileBreakpoint: 768
      },
      branding: {
        logo: '/Merit4all logo.png',
        favicon: '/Merit4all favicon.png',
        showPoweredBy: true
      }
    },
    email: {
      provider: 'firebase',
      fromAddress: 'noreply@yourdomain.com',
      templates: {
        welcome: 'welcome-email',
        passwordReset: 'password-reset',
        emailVerification: 'email-verification'
      }
    },
    storage: {
      provider: 'firebase',
      maxFileSize: 5 * 1024 * 1024,
      allowedTypes: ['image/*', 'application/pdf'],
      compression: true
    },
    performance: {
      lazyLoadModules: true,
      enableCaching: true,
      cacheExpiry: 5 * 60 * 1000,
      enableCompression: true,
      enableMinification: true
    }
  };
};

/**
 * Clear the cached settings
 */
export const clearSettingsCache = (): void => {
  cachedSettings = null;
  settingsPromise = null;
};

/**
 * Get a specific setting value by path
 * @param {string} path - Dot notation path to the setting
 * @param {*} defaultValue - Default value if setting not found
 * @returns {Promise<*>} Setting value
 */
export const getSetting = async (path: string, defaultValue: any = null): Promise<any> => {
  try {
    const settings = await getGlobalSettings();
    const keys = path.split('.');
    let value = settings;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return value !== undefined ? value : defaultValue;
  } catch (error) {
    console.error(`Error getting setting ${path}:`, error);
    return defaultValue;
  }
};

/**
 * Check if a feature is enabled
 * @param {string} featureName - Name of the feature
 * @returns {Promise<boolean>} Whether the feature is enabled
 */
export const isFeatureEnabled = async (featureName: string): Promise<boolean> => {
  return await getSetting(`features.${featureName}`, false);
};

/**
 * Check if a module is enabled
 * @param {string} moduleName - Name of the module
 * @returns {Promise<boolean>} Whether the module is enabled
 */
export const isModuleEnabled = async (moduleName: string): Promise<boolean> => {
  return await getSetting(`modules.${moduleName}.enabled`, false);
};

/**
 * Get module configuration
 * @param {string} moduleName - Name of the module
 * @returns {Promise<Object>} Module configuration
 */
export const getModuleConfig = async (moduleName: string): Promise<any> => {
  return await getSetting(`modules.${moduleName}`, null);
};

/**
 * Get app configuration
 * @returns {Promise<Object>} App configuration
 */
export const getAppConfig = async (): Promise<any> => {
  return await getSetting('app', {});
};

/**
 * Get Firebase configuration
 * @returns {Promise<Object>} Firebase configuration
 */
export const getFirebaseConfig = async (): Promise<any> => {
  return await getSetting('firebase', {});
};

/**
 * Get UI configuration
 * @returns {Promise<Object>} UI configuration
 */
export const getUIConfig = async (): Promise<any> => {
  return await getSetting('ui', {});
};

/**
 * Get security configuration
 * @returns {Promise<Object>} Security configuration
 */
export const getSecurityConfig = async (): Promise<any> => {
  return await getSetting('security', {});
};

/**
 * Get analytics configuration
 * @returns {Promise<Object>} Analytics configuration
 */
export const getAnalyticsConfig = async (): Promise<any> => {
  return await getSetting('analytics', {});
};

export default {
  getGlobalSettings,
  clearSettingsCache,
  getSetting,
  isFeatureEnabled,
  isModuleEnabled,
  getModuleConfig,
  getAppConfig,
  getFirebaseConfig,
  getUIConfig,
  getSecurityConfig,
  getAnalyticsConfig
}; 